import { useFormik } from 'formik';
import * as Yup from 'yup';
import { MessageSquare, Paperclip } from 'lucide-react';
import { ApiError } from '@/services/api/client';
import { useIssueReporters, useRaiseIssue } from '@/services/queries/useSupportQueries';
import { Input } from '@/shared/ui/input';
import { splitFormErrors, toFormErrors } from '@/shared/lib/forms';
import { cn } from '@/lib/utils';
import type { RaiseIssueRequest } from '@/types';

/**
 * Raising a support issue, laid out as the design draws it.
 *
 * ── The shape ───────────────────────────────────────────────────────────────
 *
 * A 16px card at 24px/26px, an icon tile and a heading, then two columns 32px
 * apart: what it is on the left, what happened on the right, with the attach
 * row and the submit button along the bottom of the right column. The category
 * is a grid of buttons rather than a select, because the design makes it one -
 * and it is better: six visible options are read at a glance where a closed
 * select has to be opened to find out what the choices even are.
 *
 * ── What is deliberately short ──────────────────────────────────────────────
 *
 * Somebody filling this in is already blocked and annoyed, and every extra
 * field is another chance for them to give up and phone instead - which loses
 * the written record that makes the next identical report quick to answer.
 *
 * ── "On behalf of" (owner only) ─────────────────────────────────────────────
 *
 * A lab owner may file for anyone on their licence group; everyone else files
 * for themselves. The picker shows only when there is a choice - GET
 * /issues/reporters returns more than the caller alone - and the server checks
 * the same rule again, so hiding it is presentation, not the control. The
 * issue's licence comes from the chosen person's seat, on the server.
 */
const CATEGORIES = [
  { value: 'installation', label: 'Installation' },
  { value: 'licensing', label: 'Licensing' },
  { value: 'crash', label: 'Crash' },
  { value: 'scan_import', label: 'Scan import' },
  { value: 'export', label: 'Export' },
  { value: 'other', label: 'Something else' },
] as const;

const schema = Yup.object({
  category: Yup.string().required('Pick the closest match.'),
  subject: Yup.string()
    .trim()
    .required('A one-line summary.')
    .min(3, 'A few more words - this is what support sees first.')
    .max(200, 'Keep the summary short; the detail goes below.'),
  description: Yup.string().trim().max(8000, 'That is longer than we can store.'),
  /*
   * A URL rather than a file upload. The server stores a link, not bytes - so
   * the log bundle lives wherever the lab already puts files, and NuDent never
   * becomes a place customer data is retained by accident.
   */
  attachmentUrl: Yup.string()
    .trim()
    .url('That does not look like a link. Upload the bundle somewhere and paste the URL.')
    .max(2000, 'That link is too long to store.'),
});

/** The design's label: 11.5px, weight 600, at the muted end of the ramp. */
function FieldLabel({ htmlFor, children }: { htmlFor?: string; children: React.ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-1.5 block text-[11.5px] font-semibold text-muted-foreground"
    >
      {children}
    </label>
  );
}

export function RaiseIssueForm({ onDone }: { onDone?: () => void }) {
  const raise = useRaiseIssue();
  const reporters = useIssueReporters().data ?? [];
  // A choice only exists for an owner with teammates.
  const canFileForOthers = reporters.length > 1;

  const formik = useFormik({
    // `reportedFor` empty means "me" - the server's default too.
    initialValues: {
      category: '',
      subject: '',
      description: '',
      attachmentUrl: '',
      reportedFor: '',
    },
    validationSchema: schema,
    onSubmit: (values, helpers) => {
      const body: RaiseIssueRequest = {
        category: values.category as RaiseIssueRequest['category'],
        subject: values.subject.trim(),
        // Omitted rather than sent empty: the server treats an absent
        // description as "none given", and an empty string as a given one.
        ...(values.description.trim() ? { description: values.description.trim() } : {}),
        ...(values.attachmentUrl.trim() ? { attachmentUrl: values.attachmentUrl.trim() } : {}),
        ...(values.reportedFor ? { reportedForUserId: values.reportedFor } : {}),
      };

      raise.mutate(body, {
        onSuccess: () => {
          helpers.resetForm();
          onDone?.();
        },
        onError: (error) => {
          if (error instanceof ApiError && error.fieldErrors) {
            const { placed } = splitFormErrors(toFormErrors(error.fieldErrors), values);
            helpers.setErrors(placed);
          }
        },
      });
    },
  });

  const error = (
    field: 'category' | 'subject' | 'description' | 'attachmentUrl',
  ): string | undefined => (formik.touched[field] ? formik.errors[field] : undefined);

  const apiError = raise.error instanceof ApiError ? raise.error : null;
  const banner = apiError && !apiError.fieldErrors ? apiError.message : null;

  return (
    <form
      onSubmit={formik.handleSubmit}
      noValidate
      className="rounded-2xl border border-border bg-card px-[26px] py-6 shadow-[0_1px_3px_rgba(15,23,42,0.05)]"
    >
      <div className="mb-[18px] flex items-center gap-2.5">
        <span
          className="flex size-[34px] flex-none items-center justify-center rounded-[10px] bg-brand-50 text-brand-600"
          aria-hidden="true"
        >
          <MessageSquare size={17} strokeWidth={2} />
        </span>
        <h2 className="text-[13.5px] font-semibold">Raise an issue</h2>
      </div>

      {banner && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-[12.5px] text-destructive"
        >
          {banner}
        </p>
      )}

      <div className="grid gap-8 lg:grid-cols-2">
        <div>
          {canFileForOthers && (
            <>
              <label
                htmlFor="reportedFor"
                className="mb-[7px] flex items-center gap-[7px] text-[11.5px] font-semibold text-[#48536A]"
              >
                On behalf of
                <span className="inline-flex items-center rounded-full bg-[#E2E8F0] px-[7px] py-0.5 text-[9.5px] font-semibold tracking-[0.03em] text-[#5B6B87]">
                  OWNER ONLY
                </span>
              </label>
              <div className="mb-3.5 rounded-[9px] border border-[#E3E7ED] bg-[#F7F9FC] px-[13px] py-3">
                <select
                  id="reportedFor"
                  {...formik.getFieldProps('reportedFor')}
                  className="h-9 w-full rounded-lg border-[1.5px] border-[#D7DEE8] bg-white px-2.5 text-[12.5px] text-[#1B2434] outline-none focus-visible:border-ring"
                >
                  {reporters.map((person) => (
                    <option key={person.id} value={person.isYou ? '' : person.id}>
                      {person.isYou
                        ? `${person.name} (me)`
                        : person.jobTitle
                          ? `${person.name} · ${person.jobTitle}`
                          : person.name}
                    </option>
                  ))}
                </select>
                <p className="mt-[7px] text-[10.5px] leading-[1.55] text-[#8A94A6]">
                  {/* The design adds "and they are copied on every reply" - left off
                      until support replies are actually sent to anyone. */}
                  The issue is filed against that member's seat and licence.
                </p>
              </div>
            </>
          )}

          <FieldLabel>Issue type</FieldLabel>
          {/*
            Buttons rather than a fieldset of radios, matching the design. The
            group is labelled and each button reports its own pressed state, so
            a screen reader gets the same information a radio group would give.
          */}
          <div className="grid grid-cols-2 gap-[7px]" role="group" aria-label="Issue type">
            {CATEGORIES.map((category) => {
              const selected = formik.values.category === category.value;
              return (
                <button
                  key={category.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => {
                    void formik.setFieldValue('category', category.value);
                    void formik.setFieldTouched('category', true);
                  }}
                  className={cn(
                    'h-[34px] rounded-lg border-[1.5px] px-2 text-xs transition-colors',
                    selected
                      ? 'border-brand-500 bg-muted font-semibold text-brand-600'
                      : 'border-input bg-card font-medium text-muted-foreground hover:border-brand-500/60',
                  )}
                >
                  {category.label}
                </button>
              );
            })}
          </div>
          {error('category') && (
            <p className="mt-1.5 text-xs text-destructive">{error('category')}</p>
          )}

          <div className="mt-3.5">
            <FieldLabel htmlFor="subject">Summary</FieldLabel>
            <Input
              id="subject"
              placeholder="Licence key rejected on the new workstation"
              {...formik.getFieldProps('subject')}
              aria-invalid={Boolean(error('subject'))}
              className="h-9 text-[12.5px]"
            />
            {error('subject') ? (
              <p className="mt-1.5 text-xs text-destructive">{error('subject')}</p>
            ) : (
              <p className="mt-1.5 text-[10.5px] leading-[1.55] text-muted-foreground">
                One line. This is what support sees first in the queue.
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col">
          <FieldLabel htmlFor="description">Describe what happened</FieldLabel>
          <textarea
            id="description"
            placeholder="What you were doing, what you expected, and what happened instead. Any error message, word for word."
            className="min-h-[120px] w-full flex-1 resize-none rounded-[9px] border-[1.5px] border-input bg-card px-3 py-2.5 text-[12.5px] leading-[1.55] outline-none placeholder:text-muted-foreground focus-visible:border-ring"
            {...formik.getFieldProps('description')}
            aria-invalid={Boolean(error('description'))}
          />
          {error('description') && (
            <p className="mt-1.5 text-xs text-destructive">{error('description')}</p>
          )}

          <div className="mt-3.5 flex items-center gap-3">
            {/*
              The design draws a dashed drop zone with a filename in it. This is
              a link field instead, and the dashed treatment is kept because it
              says the same thing - something optional goes here. The server
              stores a link and never the file, so NuDent does not become a
              place customer data is retained by accident.
            */}
            <div className="flex min-w-0 flex-1 items-center gap-2.5 rounded-[9px] border border-dashed border-input bg-muted/40 px-3 py-2">
              <Paperclip size={15} strokeWidth={1.9} className="flex-none text-muted-foreground" />
              <input
                id="attachmentUrl"
                aria-label="Link to a screenshot"
                placeholder="Link to a screenshot (optional)"
                {...formik.getFieldProps('attachmentUrl')}
                aria-invalid={Boolean(error('attachmentUrl'))}
                className="min-w-0 flex-1 bg-transparent text-[11.5px] outline-none placeholder:text-muted-foreground"
              />
            </div>
            <button
              type="submit"
              disabled={raise.isPending}
              className="h-10 flex-none rounded-[9px] bg-primary px-[22px] text-[13px] font-semibold text-primary-foreground hover:bg-brand-500 disabled:opacity-70"
            >
              {raise.isPending ? 'Sending…' : 'Submit issue'}
            </button>
          </div>

          {error('attachmentUrl') ? (
            <p className="mt-1.5 text-xs text-destructive">{error('attachmentUrl')}</p>
          ) : (
            <p className="mt-1.5 text-[10.5px] leading-[1.55] text-muted-foreground">
              Attach a screenshot link: upload the screenshot wherever you normally share files and
              paste the link here - we store the link, never the file.
            </p>
          )}

          {apiError?.requestId && (
            <p className="mt-1.5 font-mono text-[10.5px] text-muted-foreground">
              Reference: {apiError.requestId}
            </p>
          )}
        </div>
      </div>
    </form>
  );
}
